/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable prettier/prettier */
/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef } from 'react';
import Paper from '../Paper';
import Arrow from './Arrow';
// import Accordion from 'react-bootstrap/Accordion';

interface AccordionProps {
  title: string | React.ReactNode;
  subTitle: string | React.ReactNode;
  open: boolean;
  onClick?: () => void;
  elevation?: number;
  children?: React.ReactNode | React.ReactNode[];
  sx?: React.CSSProperties;
  customEvent?: boolean;
  mobile?: boolean;
}

/**
 *
 * @param title String or element to display in the header of the accordion
 * @param open boolean for whether to set the accordion open or closed
 * @param onClick function called when accordion header is clicked
 * @param elevation number to specify box shadow amount
 * @param sx style object to optionally override component styles
 */
const Accordion = ({
  title,
  subTitle = '',
  open,
  onClick,
  elevation = 0,
  children,
  sx,
  customEvent = false,
  mobile = false
}: AccordionProps) => {
  const [height, setHeight] = useState<null | number>(null);
  const [showSubTitle, setShowSubTitle] = useState(true);
  const [checkForResize, setcheckForResize] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    // resize when component receives children, or finds out the # of children changed
    const divHeight = ref.current?.scrollHeight ?? '300px';
    setHeight(divHeight);
  }, [children, checkForResize, customEvent]);

  useEffect(() => {
    // counts number of elements inside body and assigns value to checkForResize.
    // if checkForResize changes, fires the resize useEffect
    const handleResize = () => {
      setcheckForResize(ref?.current?.getElementsByTagName('*').length);
    };

    window.addEventListener('click', handleResize, false);
    handleResize();

    return () => window.removeEventListener('click', handleResize);
  });

  useEffect(() => {
    const handleResize = () => {
      if (ref.current?.scrollWidth > 500 || mobile) {
        setShowSubTitle(true);
      } else if (ref?.current?.scrollWidth < 300) {
        // any change to this value will accomplish what we need (the useEffect at
        // line 43 re-firing) Incrementing this value accomplished this, but it also
        // leads to a runaway loop of rapidly increasing the value by 1, leads to page lock.
        // This acts as a nonce to trigger the re-render without the runaway loop
        setcheckForResize(Math.floor(Math.random() * 100));
      } else {
        setShowSubTitle(false);
      }
    };

    window.addEventListener('resize', handleResize, false);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  });

  const handleClick = () => {
    if (onClick) onClick();
  };

  const label = (
    <div
      css={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'nowrap',
        textOverFlow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {typeof title === 'string' ? String(title).toUpperCase() : title}
      <div
        css={{
          fontSize: '14px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          maxWidth: '70%',
        }}
      >
        {!open && showSubTitle ? subTitle : ''}
      </div>
    </div>
  );

  return (
    <Paper
      elevation={elevation}
      sx={{
        height: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {/** TITLE */}
      <div
        css={{
          height: '44px',
          padding: '16px 18px 12px 18px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'row',
          overflow: 'hidden',
          gap: '18px',
          cursor: 'pointer',
          ...(sx ? sx : {}),
        }}
        onClick={handleClick}
      >
        <div
          css={{
            fontWeight: 'lighter',
            userSelect: 'none',
          }}
        >
          {label}
        </div>

        <Arrow
          sx={{
            transitionDuration: '0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </div>
      {/** END TITLE */}
      {/** BODY */}
      <div
        ref={ref}
        css={{
          overflow: 'hidden',
          transitionDuration: '0.3s',
          maxHeight: open ? `${height}px` : '0px',
        }}
      >
        {children}
      </div>
      {/** END BODY */}
    </Paper>
  );
};

export default Accordion;
